import { getToken } from "./auth";

const API_BASE = (import.meta.env.VITE_API_URL as string | undefined)
  ?? (import.meta.env.PROD ? "https://agent-wiki.sxg.workers.dev" : "");

// ---- Inline type definitions (mirrors api/src/types.ts) ----

export interface SpaceRow {
  id: string;
  owner_user_id: string;
  username: string;
  slug: string;
  name: string;
  description: string | null;
  agent_instructions: string | null;
  created_at: string;
  page_count?: number;
  pending_draft_count?: number;
}

export function updateSpace(
  username: string,
  spaceSlug: string,
  data: { name?: string; description?: string; agent_instructions?: string }
): Promise<SpaceRow> {
  return api.patch<SpaceRow>(`/api/${username}/${spaceSlug}`, data);
}

export interface PageRow {
  id: string;
  space_id: string;
  slug: string;
  title: string;
  created_at: string;
  has_draft?: number;
}

export interface PageVersionRow {
  id: string;
  page_id: string;
  content: string;
  author_type: string;
  author_id: string;
  status: string;
  parent_version_id: string | null;
  commit_message: string | null;
  approved_by: string | null;
  committed_by_type: string | null;
  committed_by: string | null;
  rejected_by: string | null;
  reject_reason: string | null;
  created_at: string;
  published_at: string | null;
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string
  ) {
    super(message);
    this.name = "ApiError";
  }
}

async function request<T>(
  path: string,
  options: RequestInit = {}
): Promise<T> {
  const token = getToken();

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(options.headers as Record<string, string>),
  };

  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers,
  });

  if (!res.ok) {
    let message = res.statusText;
    try {
      const body = (await res.json()) as { error?: string; message?: string };
      message = body.error ?? body.message ?? message;
    } catch {
      // ignore JSON parse errors
    }
    throw new ApiError(res.status, message);
  }

  // 204 No Content
  if (res.status === 204) {
    return undefined as T;
  }

  return res.json() as Promise<T>;
}

export const api = {
  get<T>(path: string): Promise<T> {
    return request<T>(path, { method: "GET" });
  },

  post<T>(path: string, body: unknown): Promise<T> {
    return request<T>(path, {
      method: "POST",
      body: JSON.stringify(body),
    });
  },

  put<T>(path: string, body: unknown): Promise<T> {
    return request<T>(path, {
      method: "PUT",
      body: JSON.stringify(body),
    });
  },

  patch<T>(path: string, body: unknown): Promise<T> {
    return request<T>(path, {
      method: "PATCH",
      body: JSON.stringify(body),
    });
  },

  delete<T>(path: string): Promise<T> {
    return request<T>(path, { method: "DELETE" });
  },
};

// ---- Spaces ----

export function listSpaces(): Promise<SpaceRow[]> {
  return api.get<SpaceRow[]>("/api/spaces");
}

export function createSpace(data: {
  slug: string;
  name: string;
  description?: string;
}): Promise<SpaceRow> {
  return api.post<SpaceRow>("/api/spaces", data);
}

export function getSpace(
  username: string,
  spaceSlug: string
): Promise<SpaceRow & { policy: SpacePolicyRow }> {
  return api.get<SpaceRow & { policy: SpacePolicyRow }>(`/api/${username}/${spaceSlug}`);
}

export function updatePolicy(
  username: string,
  spaceSlug: string,
  data: Partial<SpacePolicyRow>
): Promise<SpacePolicyRow> {
  return api.put<SpacePolicyRow>(`/api/${username}/${spaceSlug}/policy`, data);
}

export function listSpaceMembers(
  username: string,
  spaceSlug: string
): Promise<SpaceMemberRow[]> {
  return api.get<SpaceMemberRow[]>(`/api/${username}/${spaceSlug}/members`);
}

export function inviteSpaceMember(
  username: string,
  spaceSlug: string,
  targetUsername: string,
  role: string
): Promise<void> {
  return api.post<void>(`/api/${username}/${spaceSlug}/members`, { username: targetUsername, role });
}

export function removeSpaceMember(
  username: string,
  spaceSlug: string,
  userId: string
): Promise<void> {
  return api.delete<void>(`/api/${username}/${spaceSlug}/members/${userId}`);
}

// ---- Pages ----

export function listPages(
  username: string,
  spaceSlug: string
): Promise<PageRow[]> {
  return api.get<PageRow[]>(`/api/${username}/${spaceSlug}/pages`);
}

export function getPage(
  username: string,
  spaceSlug: string,
  pageSlug: string
): Promise<{ published_version: PageVersionRow | null } & PageRow> {
  return api.get<{ published_version: PageVersionRow | null } & PageRow>(
    `/api/${username}/${spaceSlug}/pages/${pageSlug}`
  );
}

export interface CreatePageInput {
  slug: string;
  title: string;
  content: string;
  commit_message?: string;
  publish?: boolean;
}

export function deletePage(
  username: string,
  spaceSlug: string,
  pageSlug: string
): Promise<void> {
  return api.delete<void>(`/api/${username}/${spaceSlug}/pages/${pageSlug}`);
}

export function listUserSpaces(username: string): Promise<SpaceRow[]> {
  return api.get<SpaceRow[]>(`/api/users/${username}/spaces`);
}

export function createPage(
  username: string,
  spaceSlug: string,
  data: CreatePageInput
): Promise<PageRow> {
  return api.post<PageRow>(`/api/${username}/${spaceSlug}/pages`, data);
}

export interface CreateVersionInput {
  content: string;
  commit_message?: string;
  parent_version_id?: string;
}

export function createVersion(
  pageId: string,
  data: CreateVersionInput
): Promise<PageVersionRow> {
  return api.post<PageVersionRow>(`/api/pages/${pageId}/versions`, data);
}

export function listVersions(pageId: string): Promise<PageVersionRow[]> {
  return api.get<PageVersionRow[]>(`/api/pages/${pageId}/versions`);
}

export function submitVersion(versionId: string): Promise<PageVersionRow> {
  return api.patch<PageVersionRow>(`/api/versions/${versionId}/submit`, {});
}

export function approveVersion(versionId: string): Promise<PageVersionRow> {
  return api.patch<PageVersionRow>(`/api/versions/${versionId}/approve`, {});
}

export function commitVersion(versionId: string): Promise<PageVersionRow> {
  return api.patch<PageVersionRow>(`/api/versions/${versionId}/commit`, {});
}

export function rejectVersion(
  versionId: string,
  reason?: string
): Promise<PageVersionRow> {
  return api.patch<PageVersionRow>(`/api/versions/${versionId}/reject`, {
    reason,
  });
}

export function updateVersionContent(
  versionId: string,
  data: { content: string; commit_message?: string }
): Promise<PageVersionRow> {
  return api.patch<PageVersionRow>(`/api/versions/${versionId}/content`, data);
}

// ---- Agent types ----

export interface AgentRow {
  id: string;
  owner_user_id: string;
  display_name: string;
  description: string | null;
  total_commits: number;
  total_rejections: number;
  created_at: string;
}

export interface AgentKeyRow {
  id: string;
  agent_id: string;
  label: string | null;
  last_used_at: string | null;
  revoked_at: string | null;
  created_at: string;
}

export interface AgentKeyCreated {
  id: string;
  key: string;
  label: string | null;
  created_at: string;
}

export interface SpacePolicyRow {
  space_id: string;
  read_policy: string;
  write_draft_policy: string;
  commit_policy: string;
  require_human_approve_before_commit: number;
  auto_commit_on_approve: number;
  approval_mode?: string;
}

export interface SpaceMemberRow {
  id: string;
  username: string;
  display_name: string | null;
  email: string;
  role: string;
}

export interface SpaceAgentRow {
  agent_id: string;
  display_name: string;
  permissions: string[];
}

// ---- Agents ----

export function createAgent(data: {
  display_name: string;
  description?: string;
}): Promise<AgentRow> {
  return api.post<AgentRow>("/api/agents", data);
}

export function listAgents(): Promise<AgentRow[]> {
  return api.get<AgentRow[]>("/api/agents");
}

export function deleteAgent(agentId: string): Promise<void> {
  return api.delete<void>(`/api/agents/${agentId}`);
}

// ---- Agent Keys ----

export function createAgentKey(
  agentId: string,
  label?: string
): Promise<AgentKeyCreated> {
  return api.post<AgentKeyCreated>(`/api/agents/${agentId}/keys`, { label });
}

export function listAgentKeys(agentId: string): Promise<AgentKeyRow[]> {
  return api.get<AgentKeyRow[]>(`/api/agents/${agentId}/keys`);
}

export function revokeAgentKey(agentId: string, keyId: string): Promise<void> {
  return api.delete<void>(`/api/agents/${agentId}/keys/${keyId}`);
}

// ---- Space Agents ----

export function listSpaceAgents(
  username: string,
  spaceSlug: string
): Promise<SpaceAgentRow[]> {
  return api.get<SpaceAgentRow[]>(`/api/${username}/${spaceSlug}/agents`);
}

export function inviteAgentToSpace(
  username: string,
  spaceSlug: string,
  agentId: string,
  permissions: string[]
): Promise<void> {
  return api.post<void>(`/api/${username}/${spaceSlug}/agents`, {
    agent_id: agentId,
    permissions,
  });
}

export function removeAgentFromSpace(
  username: string,
  spaceSlug: string,
  agentId: string
): Promise<void> {
  return api.delete<void>(`/api/${username}/${spaceSlug}/agents/${agentId}`);
}
